// @givmo/compliance — an act whose organization's sign-in check this account can't meet here
// (CCM-R1-SIGN-PASSKEY's W1 pattern; one implementation since AS-PWL-2e's level card).
//
// The gate fails such an act with `StepUpPasskeyFailure('none_registered')`: only a passkey could
// answer the refusal, and the account holds none. An emailed-code account's refusals at the stricter
// levels are this case (Account Security's AS-PWL-2e: `passkey_setup_required`, or a
// `passkey_confirmation_required` on an account whose passkey is gone). The page then says so above
// the act, and the host's offer to set up a passkey stands in the act's place: never "Please try
// again", which no retry could cure. Nothing runs by itself after a passkey is set up: the act waits
// for the person's own press, which the passkey then answers.
//
// One state per page for every act it carries, the same for each: the send page's two acts (Authorize
// and send, Authorize my AI to send it) and the level card's Save, so neither keeps a copy of its own.
// The signer page keeps its own on purpose (`signing-page.tsx`): there a passkey set up runs Sign
// again by itself, with the name Sign was pressed with, where here nothing runs until the person
// presses. The words above the act are the page's own (W1 on the send page; the level card's line),
// never this module's.

import { useCallback, useState, type ReactNode } from 'react';
import { StepUpPasskeyFailure } from '@givmo/step-up';
import { useCompliance, type PasskeySetupOutcome } from './compliance-context';

/** The gate failed the act because only a passkey could answer and the account holds none. */
export function failedForWantOfAPasskey(error: unknown): boolean {
  return error instanceof StepUpPasskeyFailure && error.reason === 'none_registered';
}

/** Which act opened the offer: the host's offer names it under "Not now". Sign keeps its own. */
export type CannotMeetCheckAct = 'send' | 'level';

export interface CannotMeetCheck {
  /** The check can't be met here: the page says so above the act. */
  readonly shown: boolean;
  /** The host's offer, drawn in the act's place while it is open; `undefined` otherwise. */
  readonly offer: ReactNode | undefined;
  /**
   * No offer to make, or one that can't work here (it settled `unavailable`): the act is shut, since
   * a press could only meet the same refusal again.
   */
  readonly actShut: boolean;
  /**
   * An act's failure, from its `onError`: `true` when it is this case, which then opens the offer
   * and says nothing else (the page shows no failure line for it).
   */
  readonly caught: (error: unknown) => boolean;
  /**
   * The act changed under the line (the level card's choice, or a save that went through): the line
   * and an open offer go, until the next refusal. An offer that settled `unavailable` stays known.
   */
  readonly reset: () => void;
}

export function useCannotMeetCheck(act: CannotMeetCheckAct): CannotMeetCheck {
  const { renderPasskeySetupOffer } = useCompliance();
  const [shown, setShown] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  // The offer settled `unavailable`: this browser or this environment can't create a passkey, so the
  // act is shut as when there is no offer at all.
  const [unavailable, setUnavailable] = useState(false);
  const caught = useCallback(
    (error: unknown) => {
      if (!failedForWantOfAPasskey(error)) return false;
      setShown(true);
      setOfferOpen(renderPasskeySetupOffer !== undefined);
      return true;
    },
    [renderPasskeySetupOffer],
  );
  const reset = useCallback(() => {
    setShown(false);
    setOfferOpen(false);
  }, []);
  const offer =
    offerOpen && renderPasskeySetupOffer !== undefined
      ? renderPasskeySetupOffer({
          act,
          // "Not now" brings the act back with the line standing; a passkey set up brings it back
          // without, and nothing runs until the person presses. An offer that can't work here shuts
          // the act, the line standing.
          onSettled: (outcome: PasskeySetupOutcome) => {
            setOfferOpen(false);
            if (outcome === 'created') setShown(false);
            else if (outcome === 'unavailable') setUnavailable(true);
          },
        })
      : undefined;
  return {
    shown,
    offer,
    actShut: shown && (renderPasskeySetupOffer === undefined || unavailable),
    caught,
    reset,
  };
}
